import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import type { BaseRatePackageView } from 'selectors/offerPage/offerPageSelectors'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferTileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import AlwaysOnPriceDetails from 'components/OfferPage/AlwaysOn/AlwaysOnPriceDetails'
import { scrollToId } from 'lib/window/scrollUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import WalledContent from 'components/Common/WalledContent'
import { min } from 'lib/array/arrayUtils'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'

const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
`

interface Props {
  offer: App.Offer
  promotion: App.OfferPackageOptionView
  checkIn?: string
  rooms: Array<App.Occupants>
  baseRatePackagesView: Array<BaseRatePackageView>
}

const DetailSegment = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
  padding: ${rem(20)} ${rem(24)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)} ${rem(20)};

  ${mediaQueryUp.desktop} {
    justify-content: space-between;
    display: grid;
    grid-template-columns: 60% 1fr;
    min-height: ${rem(260)};
  }
`

const LeftDetailSegment = styled.div`
  > * + * {
    margin-top: ${rem(16)}
  }

  ${mediaQueryUp.desktop} {
    flex: 1 0 0;
    align-self: flex-start;
  }
`

const RightDetailSegment = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: auto;
  min-width: ${rem(180)};

  ${mediaQueryUp.desktop} {
    align-self: flex-end;
    flex: 0 0 40%;
  }
`

const BuyButton = styled(TextButton)`
  margin-top: ${rem(16)};
`

function AlwaysOnOfferPromotion(props: Props) {
  const {
    offer,
    promotion,
    checkIn,
    rooms,
    baseRatePackagesView,
  } = props

  const intl = useIntl()
  const pkg = promotion.package

  const minDuration = useMemo(() => min(offer.packages.filter(p => p.hasTactical), p => p.duration)?.duration || 0, [offer.packages])

  // we send undefined for dates obviously because for intro we always should show possible inclusions
  const { inclusions: commonInclusions, bonusInclusions, tacticalInclusions } = useOfferInclusions(pkg)

  const inclusions = useMemo(() => ([...commonInclusions, ...bonusInclusions]), [bonusInclusions, commonInclusions])

  const shouldShowDiscountPercent = promotion.hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = promotion.bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = promotion.hotelDiscountPercent > promotion.bundledDiscountPercent ? promotion.hotelDiscountPercent : promotion.bundledDiscountPercent
  const shouldShowDiscountWrapper = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && !pkg.roomRate?.inclusionsHideValue

  const baseRatePackageView = useMemo(() => {
    return baseRatePackagesView.find(
      (packageItem) => packageItem.duration === pkg.duration && packageItem.id === pkg.id,
    )
  }, [baseRatePackagesView, pkg.duration, pkg.id])

  const baseRateValue = baseRatePackageView?.value

  const valuedUpTo = useMemo(() => {
    const inclusionsAmount = pkg.inclusionsAmount
    const propertyFees = pkg.propertyFees
    if (baseRateValue && (baseRateValue + inclusionsAmount) > pkg.value) {
      const displayedHotelValue = baseRateValue + inclusionsAmount + propertyFees
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, promotion.hotelPrice)

      if (displayedHotelDiscountPercent > highestDiscountPercent) {
        return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
      }
    }

    return { value: promotion.hotelValue, percent: highestDiscountPercent }
  }, [pkg.inclusionsAmount, pkg.propertyFees, pkg.value, baseRateValue, promotion.hotelValue, promotion.hotelPrice, highestDiscountPercent])

  const { travelFrom, travelTo } = useMemo(() => {
    const tacticalSchedule = promotion?.package.schedules?.find(x => x.type === 'tactical')
    let travelFrom: string | null | undefined = tacticalSchedule?.travelPeriod?.from
    let travelTo: string | null | undefined = tacticalSchedule?.travelPeriod?.to
    if ((!travelFrom || !travelTo) && tacticalInclusions.length > 0) {
      const tacticalInclusionsSchedule = tacticalInclusions[0].schedules?.find(x => x.type === 'tactical')
      travelFrom = tacticalInclusionsSchedule?.travelPeriod?.from
      travelTo = tacticalInclusionsSchedule?.travelPeriod?.to
    }

    return { travelFrom, travelTo }
  }, [promotion?.package.schedules, tacticalInclusions])

  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(pkg.duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, pkg.duration])

  return (
    <DetailSegment>
      <LeftDetailSegment>
        <ProductTypeLabel productType="limited_time_special" />
        <Group direction="vertical" gap={8}>
          <VerticalSpacer gap={4}>
            <Heading variant="heading5">{pkg.name}</Heading>
            <TAOCancellationPolicies
              offerId={offer.id}
              useDynamicCancellationPolicies={offer.property?.useDynamicCancellationPolicies && !pkg.ignoreDynamicCancellationPolicy}
              timezoneOffset={offer.property?.timezoneOffset}
              uniqueKey={pkg.uniqueKey}
              checkInDate={checkIn}
              checkOutDate={checkOut}
              occupants={rooms}
              cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
              offerType={offer.type}
            />
          </VerticalSpacer>
          <OfferTileInclusions
            offerTitle={offer.property?.name ? offer.property.name : offer.name}
            heading="Your handpicked inclusions"
            tileInclusionsList={inclusions}
            maxInclusionsCount={3}
          />
        </Group>
        <TravelPeriodSection>
          <BodyText variant="small" weight="normal" colour="neutral-two">
            {[
              travelFrom && travelTo && intl.formatMessage(
                {
                  id: 'zvkO5o77HP1ue_sqvcRB',
                  defaultMessage: 'Travel between: {startDate} - {endDate}.',
                  description: 'Travel date range showing start and end dates for the promotional tactical rate on the offer page',
                },
                {
                  startDate: moment(travelFrom).format(DMY_CASUAL_SHORT_FORMAT),
                  endDate: moment(travelTo).subtract(pkg.duration, 'days').format(DMY_CASUAL_SHORT_FORMAT),
                },
              ),
              !!minDuration && intl.formatMessage(
                {
                  id: '-mCT_yOBup_zWEFuQJlL',
                  defaultMessage: 'Minimum stay: {nights, plural, one {# night} other {# nights}}',
                  description: 'Minimum number of nights stay requirement for the promotional tactical rate on the offer page',
                },
                { nights: minDuration },
              ),
            ].filter(Boolean).join(' ')}
          </BodyText>
        </TravelPeriodSection>
      </LeftDetailSegment>
      <RightDetailSegment>
        <WalledContent enforced>
          {shouldShowDiscountWrapper && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={valuedUpTo.percent}
          />}
          <AlwaysOnPriceDetails
            rooms={rooms}
            checkIn={checkIn}
            packageView={promotion}
            offer={offer}
            canBuyNow={false}
            valuedUpTo={valuedUpTo}
          />
        </WalledContent>
        <BuyButton
          kind="primary"
          data-filter="checkin"
          onClick={() => scrollToId(pkg?.roomType?.id ?? '')}
          fit="full-width"
        >
          {intl.formatMessage({
            id: 'VVbKoSQ3AEdWT-gZd91A',
            defaultMessage: 'Check availability',
            description: 'Button text to check availability on always-on offer promotion',
          })}
        </BuyButton>
      </RightDetailSegment>
    </DetailSegment>
  )
}

export default React.memo(AlwaysOnOfferPromotion)
